import React, { useEffect, useState } from 'react';
import API from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, RefreshCw } from 'lucide-react';

function CardDialog({ open, onClose, onSave, initial, cardholders }) {
  const [form, setForm] = useState({ cardholder_id: '', masked_pan: '', card_last4: '', card_type: 'Visa', expiry_month: '', expiry_year: '' });
  useEffect(() => { if (open) setForm(initial ? { ...initial, expiry_month: initial.expiry_month || '', expiry_year: initial.expiry_year || '' } : { cardholder_id: '', masked_pan: '', card_last4: '', card_type: 'Visa', expiry_month: '', expiry_year: '' }); }, [open, initial]);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-md">
        <DialogHeader><DialogTitle>{initial ? 'Edit' : 'Add'} Card</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label className="text-xs">Cardholder *</Label>
            <Select value={form.cardholder_id} onValueChange={v => set('cardholder_id', v)}>
              <SelectTrigger className="mt-1 h-8 text-sm"><SelectValue placeholder="Select cardholder" /></SelectTrigger>
              <SelectContent className="bg-white">{cardholders.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Masked PAN (e.g. 5221 **** **** 9384)</Label><Input value={form.masked_pan} onChange={e=>set('masked_pan',e.target.value)} className="mt-1 h-8 text-sm font-mono" data-testid="card-pan-input" /></div>
          <div><Label className="text-xs">Last 4 Digits</Label><Input value={form.card_last4} onChange={e=>set('card_last4',e.target.value)} className="mt-1 h-8 text-sm font-mono" maxLength={4} /></div>
          <div><Label className="text-xs">Card Type</Label>
            <Select value={form.card_type} onValueChange={v => set('card_type', v)}>
              <SelectTrigger className="mt-1 h-8 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                {['Visa','Mastercard','Amex','Diners'].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs">Expiry Month</Label><Input type="number" min={1} max={12} value={form.expiry_month} onChange={e=>set('expiry_month',e.target.value)} className="mt-1 h-8 text-sm" /></div>
            <div><Label className="text-xs">Expiry Year</Label><Input type="number" min={2024} value={form.expiry_year} onChange={e=>set('expiry_year',e.target.value)} className="mt-1 h-8 text-sm" /></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" onClick={() => onSave(form)}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function CardsPage() {
  const [cards, setCards] = useState([]);
  const [cardholders, setCardholders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [cr, chr] = await Promise.all([API.get('/cards'), API.get('/cardholders')]);
      setCards(cr.data); setCardholders(chr.data);
    } catch(e) { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const chMap = Object.fromEntries(cardholders.map(c => [c.id, c.name]));

  const save = async (form) => {
    try {
      const payload = { ...form, expiry_month: form.expiry_month ? parseInt(form.expiry_month) : null, expiry_year: form.expiry_year ? parseInt(form.expiry_year) : null };
      if (editing) { await API.put(`/cards/${editing.id}`, payload); toast.success('Updated'); }
      else { await API.post('/cards', payload); toast.success('Created'); }
      setDialogOpen(false); setEditing(null); load();
    } catch(e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const doDelete = async () => {
    try { await API.delete(`/cards/${deleteId}`); toast.success('Deleted'); setDeleteId(null); load(); }
    catch(e) { toast.error('Delete failed'); }
  };

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold">Cards</h1>
        <Button size="sm" onClick={() => { setEditing(null); setDialogOpen(true); }} data-testid="add-card-btn">
          <Plus size={14} className="mr-1" /> Add Card
        </Button>
      </div>
      <div className="bg-white rounded-lg border shadow-sm overflow-x-auto">
        <table className="data-table" data-testid="cards-table">
          <thead><tr>
            <th>Masked PAN</th><th>Last 4</th><th>Type</th><th>Cardholder</th><th>Expiry</th><th>Status</th><th className="text-right">Actions</th>
          </tr></thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="text-center py-8 text-slate-400"><RefreshCw size={16} className="animate-spin inline" /></td></tr>
            ) : cards.length === 0 ? (
              <tr><td colSpan={7} className="text-center py-8 text-slate-400">No cards added yet</td></tr>
            ) : cards.map(c => (
              <tr key={c.id}>
                <td className="font-mono text-sm">{c.masked_pan}</td>
                <td className="font-mono text-sm">••••{c.card_last4}</td>
                <td>{c.card_type}</td>
                <td>{chMap[c.cardholder_id] || c.cardholder_id}</td>
                <td>{c.expiry_month && c.expiry_year ? `${String(c.expiry_month).padStart(2,'0')}/${c.expiry_year}` : '—'}</td>
                <td><span className={`text-xs px-2 py-0.5 rounded-full border ${c.is_active !== false ? 'chip-approved' : 'chip-rejected'}`}>{c.is_active !== false ? 'Active' : 'Inactive'}</span></td>
                <td className="text-right">
                  <button onClick={() => { setEditing(c); setDialogOpen(true); }} className="p-1 text-slate-400 hover:text-emerald-600 mr-1"><Pencil size={13} /></button>
                  <button onClick={() => setDeleteId(c.id)} className="p-1 text-slate-400 hover:text-rose-600"><Trash2 size={13} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <CardDialog open={dialogOpen} onClose={() => { setDialogOpen(false); setEditing(null); }} onSave={save} initial={editing} cardholders={cardholders} />
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader><AlertDialogTitle>Delete Card?</AlertDialogTitle><AlertDialogDescription>This will remove the card record.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-rose-600">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
